/**
 * Мультивыбор с поиском. Один компонент вместо пяти дропдаунов макета:
 * исполнители при создании и при редактировании задачи, участники доски
 * при её создании, ответственный столбца, теги задачи.
 *
 * Все пять в макете имеют одинаковую форму: поиск, сводная подпись выбранного,
 * список с множественным выбором. Поиск ведётся в самом поле.
 */

import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

import { cn } from "./cn";
import { OverflowMarquee } from "./OverflowMarquee";

export interface PickerOption {
  id: string;
  label: string;
  /** Дополнительная строка: должность, код проекта, подразделение. */
  hint?: string;
  disabled?: boolean;
  /** Архивные элементы справочника остаются у существующих объектов,
   *  но не предлагаются новым (§7.1). */
  archived?: boolean;
  /** Уровень вложенности в дереве (подразделения) — отступ в списке. */
  depth?: number;
}

interface MultiSelectPickerProps {
  options: PickerOption[];
  value: string[];
  onChange: (value: string[]) => void;
  /** Подпись на кнопке, когда ничего не выбрано. */
  placeholder: string;
  /** Доступное имя поля. */
  label: string;
  /** Своя отрисовка строки — например, аватар рядом с именем. */
  renderOption?: (option: PickerOption, selected: boolean) => ReactNode;
  /** Сводка выбранного. По умолчанию: имя, либо «выбрано N». */
  summarize?: (selected: PickerOption[]) => string;
  disabled?: boolean;
  invalid?: boolean;
  emptyText?: string;
  id?: string;
}

function defaultSummary(selected: PickerOption[]): string {
  if (selected.length === 0) return "";
  if (selected.length === 1) return selected[0]?.label ?? "";
  if (selected.length === 2) return selected.map((o) => o.label).join(", ");
  return `Выбрано: ${String(selected.length)}`;
}

export function MultiSelectPicker({
  options,
  value,
  onChange,
  placeholder,
  label,
  renderOption,
  summarize = defaultSummary,
  disabled = false,
  invalid = false,
  emptyText = "Ничего не найдено",
  id,
}: MultiSelectPickerProps) {
  const [query, setQuery] = useState("");

  const selected = useMemo(
    () => options.filter((option) => value.includes(option.id)),
    [options, value],
  );

  const visible = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    // Архивный элемент не предлагается новым, но остаётся видимым,
    // если уже выбран — иначе он молча исчезнет при редактировании (§7.1).
    const available = options.filter(
      (option) => option.archived !== true || value.includes(option.id),
    );
    if (normalized.length === 0) return available;
    return available.filter(
      (option) =>
        option.label.toLowerCase().includes(normalized) ||
        (option.hint ?? "").toLowerCase().includes(normalized),
    );
  }, [options, query, value]);

  const summary = summarize(selected);

  // Поле поиска — сам элемент управления, а не строка внутри панели.
  // Headless UI позиционирует панель относительно ComboboxInput и берёт
  // ширину из --input-width. Прежде поле жило внутри панели: панель
  // привязывалась к самой себе, уезжала к краю окна и сжималась до
  // нескольких пикселей — выбрать ничего было нельзя ни в диалоге создания
  // доски, ни в фильтрах, ни в карточке задачи.
  return (
    <Combobox
      multiple
      immediate
      value={value}
      onChange={(next: string[]) => {
        onChange(next);
        setQuery("");
      }}
      disabled={disabled}
      // Запрос сбрасывается при закрытии: иначе следующее открытие покажет
      // отфильтрованный список без видимой причины.
      onClose={() => setQuery("")}
    >
      <div className="relative">
        <ComboboxInput
          id={id}
          aria-label={label}
          displayValue={() => query}
          onChange={(event) => setQuery(event.target.value)}
          // Пока ничего не набрано, поле показывает выбранное — обычным цветом.
          placeholder={summary || placeholder}
          title={summary || undefined}
          className={cn(
            "h-9 w-full truncate rounded-md border bg-surface pr-9 pl-3 text-sm text-fg outline-none transition-colors",
            "disabled:cursor-not-allowed disabled:opacity-60",
            summary ? "placeholder:text-fg" : "placeholder:text-faint",
            invalid ? "border-danger" : "border-line focus:border-accent data-open:border-accent",
          )}
        />
        <ComboboxButton
          aria-label={`Открыть список: ${label}`}
          className="absolute inset-y-0 right-0 flex items-center px-2.5 disabled:cursor-not-allowed"
        >
          <ChevronDown className="size-4 shrink-0 text-faint" aria-hidden />
        </ComboboxButton>
      </div>

      <ComboboxOptions
        anchor={{ to: "bottom start", gap: 4, padding: 8 }}
        // Не модальный: по умолчанию Headless UI делает всё вокруг открытого
        // списка недоступным (inert). В диалоге создания доски это блокировало
        // «Создать», пока список участников открыт, — кнопка «не реагировала».
        // Выбор нескольких значений держит список открытым, так что щелчок
        // снаружи обязан и закрывать его, и доходить до цели.
        modal={false}
        // Высота — через --anchor-max-height: Headless UI пишет в style
        // max-height = min(этой переменной, места на экране), и класс max-h-*
        // им перебивается. Прежде длинный список растягивался на весь экран
        // и накрывал форму, в которой открыт.
        className="z-50 w-(--input-width) rounded-md border border-line bg-raised p-1 shadow-md [--anchor-max-height:16rem] focus:outline-none"
      >
        {visible.length === 0 ? (
          <p className="px-2.5 py-3 text-center text-[13px] text-faint">{emptyText}</p>
        ) : (
          visible.map((option) => (
            <ComboboxOption
              key={option.id}
              value={option.id}
              disabled={option.disabled === true}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-1.5 text-[13px]",
                // Список остаётся привязанным к полю (в нём набирают поиск,
                // а лист снизу ушёл бы под клавиатуру), но строки — под палец.
                "pointer-coarse:min-h-11 pointer-coarse:text-[15px]",
                "text-fg data-focus:bg-sunken",
                "data-disabled:cursor-not-allowed data-disabled:opacity-40",
              )}
            >
              {({ selected: isSelected }) => (
                <>
                  <Check
                    className={cn(
                      "size-4 shrink-0 text-accent",
                      isSelected ? "opacity-100" : "opacity-0",
                    )}
                    aria-hidden
                  />
                  {renderOption ? (
                    renderOption(option, isSelected)
                  ) : (
                    <OverflowMarquee>
                      {option.label}
                      {option.hint !== undefined && (
                        <span className="ml-1.5 text-faint">{option.hint}</span>
                      )}
                      {option.archived === true && (
                        <span className="ml-1.5 text-faint italic">архивный</span>
                      )}
                    </OverflowMarquee>
                  )}
                </>
              )}
            </ComboboxOption>
          ))
        )}
      </ComboboxOptions>
    </Combobox>
  );
}
